import { useState } from 'react'

import type { AutomationStatus, Workflow } from '@/api/automation'
import { NotLivePage, useNotLivePage } from '@/app/notLive'
import { Card, DataTable, EmptyState, formatInteger, formatRelativeTime, LoadingBlock, SegmentedControl, useNow, type Column } from '@/design'
import { useFilterSearch } from '@/lib/useGlobalFilters'

import './automation.css'
import { AutomationStatusBadge } from './badges'
import { useAutomationQuery } from './hooks'

const STATUS_OPTIONS: ReadonlyArray<{ value: AutomationStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'running', label: 'Running' },
  { value: 'paused', label: 'Paused' },
  { value: 'draft', label: 'Draft' },
]

function columns(now: Date): Column<Workflow>[] {
  return [
    { key: 'name', header: 'Workflow', sortValue: (row) => row.name, cell: (row) => row.name },
    { key: 'status', header: 'Status', cell: (row) => <AutomationStatusBadge status={row.status} /> },
    { key: 'trigger', header: 'Trigger', cell: (row) => row.trigger.detail },
    { key: 'runs', header: 'Runs', align: 'end', sortValue: (row) => row.runCount, cell: (row) => formatInteger(row.runCount) },
    {
      key: 'lastRun',
      header: 'Last run',
      sortValue: (row) => row.lastRunAt ?? '',
      cell: (row) => (row.lastRunAt ? formatRelativeTime(row.lastRunAt, now) : 'Never'),
    },
  ]
}

/** Automation > Workflows: every automation and its current run stats, filterable by status. */
export function WorkflowsPage() {
  const notLive = useNotLivePage('workflows')
  const [status, setStatus] = useState<AutomationStatus | 'all'>('all')
  const workflows = useAutomationQuery('workflows', (client) => client.workflows())
  const search = useFilterSearch()
  const now = useNow()

  const all = workflows.data?.data ?? []
  const items = status === 'all' ? all : all.filter((workflow) => workflow.status === status)

  if (notLive) return <NotLivePage />

  return (
    <div className="ci-stack">
      <div className="au-filter-row">
        <SegmentedControl label="Filter by status" options={STATUS_OPTIONS} value={status} onChange={setStatus} />
      </div>

      <Card title="Workflows" subtitle="Automations built from a trigger, conditions and actions." busy={workflows.isFetching}>
        {workflows.isPending ? <LoadingBlock rows={6} /> : null}
        {workflows.isError ? <EmptyState variant="outage" compact description="Workflows could not be loaded." /> : null}
        {workflows.data && all.length === 0 ? (
          <EmptyState variant="no-data" compact title="No workflows yet" description="Automations you create will appear here." />
        ) : null}
        {workflows.data && all.length > 0 && items.length === 0 ? (
          <EmptyState variant="no-results" compact title="No workflows at this status" description="Try a different status, or choose All." />
        ) : null}
        {workflows.data && items.length > 0 ? (
          <DataTable
            caption="Workflows"
            columns={columns(now)}
            rows={items}
            rowKey={(row) => row.id}
            rowHref={(row) => `/automation/workflows/${row.id}${search}`}
            initialSort={{ key: 'runs', direction: 'desc' }}
          />
        ) : null}
      </Card>
    </div>
  )
}
